import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import DataTable from "react-data-table-component";
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Row,
  Badge,
  Button,
} from "reactstrap";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import {
  getActivePhoneOtpList,
  getPendingCinPanList,
  reviewCinPan,
  updateSpaceStatus,
} from "./helper/businessVerificationApi";

const formatDateTime = (value) => {
  if (!value) return "-";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? "-" : d.toLocaleString();
};

const BusinessVerificationPage = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [phoneOtpData, setPhoneOtpData] = useState([]);
  const [phoneOtpLoading, setPhoneOtpLoading] = useState(false);
  const [actionLoadingMap, setActionLoadingMap] = useState({});

  const abortControllerRef = useRef(null);
  const phoneOtpAbortControllerRef = useRef(null);
  const isMountedRef = useRef(true);
  const isLoadingRef = useRef(false);
  const isPhoneOtpLoadingRef = useRef(false);
  const hasInitialFetchedRef = useRef(false);
  const hasInitialPhoneOtpFetchedRef = useRef(false);

  const setRowLoading = useCallback((spaceId, isLoadingValue) => {
    setActionLoadingMap((prev) => ({
      ...prev,
      [spaceId]: isLoadingValue,
    }));
  }, []);

  const fetchList = useCallback(async () => {
    if (isLoadingRef.current) return;

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    isLoadingRef.current = true;
    setLoading(true);

    try {
      const result = await getPendingCinPanList();

      if (abortController.signal.aborted || !isMountedRef.current) {
        return;
      }

      setData(Array.isArray(result.data) ? result.data : []);
    } catch (error) {
      if (error.name !== "AbortError" && isMountedRef.current) {
        console.error("Error fetching pending CIN/PAN list:", error);
      }
    } finally {
      if (isMountedRef.current && !abortController.signal.aborted) {
        setLoading(false);
        isLoadingRef.current = false;
      }
      if (abortControllerRef.current === abortController) {
        abortControllerRef.current = null;
      }
    }
  }, []);

  const fetchPhoneOtpList = useCallback(async () => {
    if (isPhoneOtpLoadingRef.current) return;

    if (phoneOtpAbortControllerRef.current) {
      phoneOtpAbortControllerRef.current.abort();
    }

    const abortController = new AbortController();
    phoneOtpAbortControllerRef.current = abortController;

    isPhoneOtpLoadingRef.current = true;
    setPhoneOtpLoading(true);

    try {
      const result = await getActivePhoneOtpList();

      if (abortController.signal.aborted || !isMountedRef.current) {
        return;
      }

      setPhoneOtpData(Array.isArray(result.data) ? result.data : []);
    } catch (error) {
      if (error.name !== "AbortError" && isMountedRef.current) {
        console.error("Error fetching active phone OTP list:", error);
      }
    } finally {
      if (isMountedRef.current && !abortController.signal.aborted) {
        setPhoneOtpLoading(false);
        isPhoneOtpLoadingRef.current = false;
      }
      if (phoneOtpAbortControllerRef.current === abortController) {
        phoneOtpAbortControllerRef.current = null;
      }
    }
  }, []);

  const handleAccept = useCallback(
    async (row) => {
      const spaceId = row?.space_id;
      if (!spaceId) {
        alert("space_id is missing");
        return;
      }

      setRowLoading(spaceId, true);
      try {
        await reviewCinPan({ spaceId, action: "accepted" });
        await fetchList();
      } catch (error) {
        console.error("Error accepting CIN/PAN:", error);
        alert(error?.response?.data?.message || error.message || "Failed to accept request");
      } finally {
        setRowLoading(spaceId, false);
      }
    },
    [fetchList, setRowLoading]
  );

  const handleReject = useCallback(
    async (row) => {
      const spaceId = row?.space_id;
      if (!spaceId) {
        alert("space_id is missing");
        return;
      }

      const rejectionReason = window.prompt("Enter rejection reason:");
      if (rejectionReason === null) {
        return;
      }
      if (!rejectionReason.trim()) {
        alert("rejectionReason is required");
        return;
      }

      setRowLoading(spaceId, true);
      try {
        await reviewCinPan({
          spaceId,
          action: "rejected",
          rejectionReason: rejectionReason.trim(),
        });
        await fetchList();
      } catch (error) {
        console.error("Error rejecting CIN/PAN:", error);
        alert(error?.response?.data?.message || error.message || "Failed to reject request");
      } finally {
        setRowLoading(spaceId, false);
      }
    },
    [fetchList, setRowLoading]
  );

  const handleSpaceStatusChange = useCallback(
    async (row, status) => {
      const spaceId = row?.space_id;
      if (!spaceId) {
        alert("space_id is missing");
        return;
      }

      setRowLoading(spaceId, true);
      try {
        await updateSpaceStatus({ spaceId, status });
        await fetchList();
      } catch (error) {
        console.error("Error updating business space status:", error);
        alert(
          error?.response?.data?.message ||
            error.message ||
            "Failed to update business space status"
        );
      } finally {
        setRowLoading(spaceId, false);
      }
    },
    [fetchList, setRowLoading]
  );

  const columns = useMemo(
    () => [
      {
        name: <span className="font-weight-bold fs-13">Space ID</span>,
        selector: (row) => row.space_id,
        sortable: true,
        cell: (row) => (
          <span className="font-monospace small">{row.space_id || "-"}</span>
        ),
      },
      {
        name: <span className="font-weight-bold fs-13">Space Name</span>,
        selector: (row) => row.business_space?.space_name,
        sortable: true,
        cell: (row) => row.business_space?.space_name || "-",
      },
      {
        name: <span className="font-weight-bold fs-13">Owner ID</span>,
        selector: (row) => row.business_space?.owner_id,
        sortable: true,
        cell: (row) => (
          <span className="font-monospace small">
            {row.business_space?.owner_id || "-"}
          </span>
        ),
      },
      {
        name: <span className="font-weight-bold fs-13">PAN</span>,
        selector: (row) => row.pan,
        sortable: true,
        cell: (row) => row.pan || "-",
      },
      {
        name: <span className="font-weight-bold fs-13">CIN</span>,
        selector: (row) => row.cin,
        sortable: true,
        cell: (row) => row.cin || "-",
      },
      {
        name: <span className="font-weight-bold fs-13">Space Status</span>,
        selector: (row) => row.business_space?.status,
        sortable: true,
        cell: (row) => {
          const status = row.business_space?.status || "unknown";
          if (status === "verified") {
            return <Badge color="success">{status}</Badge>;
          }
          if (status === "rejected") {
            return <Badge color="danger">{status}</Badge>;
          }
          return <Badge color="warning">{status}</Badge>;
        },
      },
      {
        name: <span className="font-weight-bold fs-13">Requested At</span>,
        selector: (row) => row.requested_at,
        sortable: true,
        cell: (row) => {
          if (!row.requested_at) return "-";
          return new Date(row.requested_at).toLocaleString();
        },
      },
      {
        name: <span className="font-weight-bold fs-13">History</span>,
        selector: (row) => row.has_previous_verification_request,
        cell: (row) => {
          const count = Array.isArray(row.previous_submissions)
            ? row.previous_submissions.length
            : 0;
          if (row.has_previous_verification_request) {
            return <Badge color="info">{count} previous</Badge>;
          }
          return "-";
        },
      },
      {
        name: <span className="font-weight-bold fs-13">Actions</span>,
        cell: (row) => {
          const busy = Boolean(actionLoadingMap[row.space_id]);
          return (
            <div className="d-flex gap-2 flex-wrap">
              <Button
                color="success"
                size="sm"
                disabled={busy}
                onClick={() => handleAccept(row)}
              >
                Accept
              </Button>
              <Button
                color="danger"
                size="sm"
                disabled={busy}
                onClick={() => handleReject(row)}
              >
                Reject
              </Button>
              <Button
                color="secondary"
                size="sm"
                disabled={busy}
                onClick={() => handleSpaceStatusChange(row, "pending")}
              >
                Set Pending
              </Button>
              <Button
                color="warning"
                size="sm"
                disabled={busy}
                onClick={() => handleSpaceStatusChange(row, "rejected")}
              >
                Set Rejected
              </Button>
              <Button
                color="primary"
                size="sm"
                disabled={busy}
                onClick={() => handleSpaceStatusChange(row, "verified")}
              >
                Set Verified
              </Button>
            </div>
          );
        },
        ignoreRowClick: true,
        allowOverflow: true,
        button: true,
      },
    ],
    [actionLoadingMap, handleAccept, handleReject, handleSpaceStatusChange]
  );

  const phoneOtpColumns = useMemo(
    () => [
      {
        name: <span className="font-weight-bold fs-13">Space Name</span>,
        selector: (row) => row.business_space?.space_name,
        sortable: true,
        cell: (row) => row.business_space?.space_name || "-",
      },
      {
        name: <span className="font-weight-bold fs-13">Space ID</span>,
        selector: (row) => row.space_id,
        sortable: true,
        cell: (row) => (
          <span className="font-monospace small">{row.space_id || "-"}</span>
        ),
      },
      {
        name: <span className="font-weight-bold fs-13">Phone (last 4)</span>,
        selector: (row) => row.phone_last4,
        sortable: true,
        cell: (row) => row.phone_last4 || "-",
      },
      {
        name: <span className="font-weight-bold fs-13">OTP</span>,
        selector: (row) => row.otp,
        sortable: true,
        cell: (row) => (
          <span className="font-monospace fw-semibold">{row.otp || "-"}</span>
        ),
      },
      {
        name: <span className="font-weight-bold fs-13">Expires At</span>,
        selector: (row) => row.otp_expires_at,
        sortable: true,
        cell: (row) => formatDateTime(row.otp_expires_at),
      },
      {
        name: <span className="font-weight-bold fs-13">Last Sent</span>,
        selector: (row) => row.otp_last_sent_at,
        sortable: true,
        cell: (row) => formatDateTime(row.otp_last_sent_at),
      },
      {
        name: <span className="font-weight-bold fs-13">Attempts</span>,
        selector: (row) => row.otp_attempt_count,
        sortable: true,
        cell: (row) => row.otp_attempt_count ?? 0,
      },
      {
        name: <span className="font-weight-bold fs-13">Status</span>,
        selector: (row) => row.phone_verified_status,
        sortable: true,
        cell: (row) => (
          <Badge color="warning" className="font-size-12">
            {row.phone_verified_status || "pending"}
          </Badge>
        ),
      },
    ],
    []
  );

  useEffect(() => {
    isMountedRef.current = true;
    if (!hasInitialFetchedRef.current) {
      fetchList();
      hasInitialFetchedRef.current = true;
    }
    if (!hasInitialPhoneOtpFetchedRef.current) {
      fetchPhoneOtpList();
      hasInitialPhoneOtpFetchedRef.current = true;
    }

    return () => {
      isMountedRef.current = false;
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      if (phoneOtpAbortControllerRef.current) {
        phoneOtpAbortControllerRef.current.abort();
      }
    };
  }, [fetchList, fetchPhoneOtpList]);

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs
            title="Business Verification"
            breadcrumbItem="Business Verification"
          />
          <Row>
            <Col xs={12}>
              <Card>
                <CardHeader className="d-flex justify-content-between align-items-center">
                  <h4 className="card-title mb-0">
                    <i className="mdi mdi-domain me-2"></i>
                    Pending CIN/PAN Business Verifications
                  </h4>
                  <Button color="info" size="sm" onClick={fetchList} disabled={loading}>
                    Refresh
                  </Button>
                </CardHeader>
                <CardBody>
                  <DataTable
                    columns={columns}
                    data={data}
                    progressPending={loading}
                    pagination
                    paginationServer={false}
                    paginationPerPage={50}
                    paginationRowsPerPageOptions={[10, 25, 50, 100]}
                    noDataComponent={
                      <div className="text-center py-4">
                        <i
                          className="mdi mdi-domain-off"
                          style={{ fontSize: "3rem", color: "#6c757d" }}
                        ></i>
                        <p className="mt-3 text-muted">
                          No pending CIN/PAN business verifications found
                        </p>
                      </div>
                    }
                    highlightOnHover
                    pointerOnHover
                  />
                </CardBody>
              </Card>
            </Col>
          </Row>
          <Row className="mt-4">
            <Col xs={12}>
              <Card>
                <CardHeader className="d-flex justify-content-between align-items-center">
                  <h4 className="card-title mb-0">
                    <i className="mdi mdi-cellphone-key me-2"></i>
                    Active Phone OTPs
                  </h4>
                  <Button
                    color="info"
                    size="sm"
                    onClick={fetchPhoneOtpList}
                    disabled={phoneOtpLoading}
                  >
                    Refresh
                  </Button>
                </CardHeader>
                <CardBody>
                  <DataTable
                    columns={phoneOtpColumns}
                    data={phoneOtpData}
                    progressPending={phoneOtpLoading}
                    pagination
                    paginationServer={false}
                    paginationPerPage={50}
                    paginationRowsPerPageOptions={[10, 25, 50, 100]}
                    noDataComponent={
                      <div className="text-center py-4">
                        <i
                          className="mdi mdi-cellphone-off"
                          style={{ fontSize: "3rem", color: "#6c757d" }}
                        ></i>
                        <p className="mt-3 text-muted">
                          No active phone OTPs (non-expired) found
                        </p>
                      </div>
                    }
                    highlightOnHover
                    pointerOnHover
                  />
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default BusinessVerificationPage;
